import React from 'react';
import ItemCard from 'tradera-components/item-card/item-card';
import ItemListCard from 'tradera-components/item-card/item-list-card';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINT_SIZES } from 'tradera-constants/breakpoints';
import { DesktopAd, TabletAd, MobileAd } from './ads/search-results-ads';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import useIsMobile from 'tradera-hooks/use-is-mobile';

const DESKTOP_AD_OFFSET = 0;
const TABLET_AD_OFFSET = 0;
const MOBILE_AD_OFFSET = 6;
const AD_FREQUENCY = 12;
const AD_FREQUENCY_FULL_WIDTH = 16;

const getAdFrequency = (isExtraLargeDesktop, itemLayout, siteLayout) => {
    if (
        isExtraLargeDesktop &&
        itemLayout === 'layout-grid' &&
        siteLayout === 'site-width-full'
    ) {
        return AD_FREQUENCY_FULL_WIDTH;
    } else {
        return AD_FREQUENCY;
    }
};

const ItemsWithStitchedAds = ({
    adsEnabled,
    handleAddToWishlist,
    handleQuickViewClick,
    handleRemoveFromWishlist,
    handleOpenItemSettings,
    handleItemAttributesForFilterClick,
    itemLayout,
    items,
    pageIndex,
    quickViewEnabled,
    siteLayout,
    skipTopAd,
    isShopOwnedByCurrentMember
}) => {
    const ItemCardComponent =
        itemLayout === 'layout-list' ? ItemListCard : ItemCard;

    const shouldUseNaturalImageMode =
        useIsMobile() && itemLayout === 'layout-grid-large';

    const imageMode = shouldUseNaturalImageMode ? 'NATURAL' : 'SQUARE';

    if (!(items && items.length > 0)) {
        return null;
    }

    return (
        <ComponentsWithStitchAds
            {...{
                adsEnabled,
                itemLayout,
                pageIndex,
                siteLayout,
                skipTopAd
            }}
            components={items.map(item => (
                <div
                    key={item.itemId}
                    className={`item-card-container ${
                        itemLayout === 'layout-list'
                            ? 'pb-2 mb-2 border-bottom'
                            : ''
                    }`}>
                    <ItemCardComponent
                        className={
                            itemLayout === 'layout-list' ? '' : 'mr-md-1 mb-1'
                        }
                        imageMode={imageMode}
                        item={item}
                        lazy={lazyLoadSettings}
                        onAddToWishlist={handleAddToWishlist}
                        onAttributeButtonClick={
                            handleItemAttributesForFilterClick
                        }
                        onQuickView={quickViewEnabled && handleQuickViewClick}
                        onRemoveFromWishlist={handleRemoveFromWishlist}
                        onSettings={handleOpenItemSettings}
                        ready={!!item.itemUrl}
                        isShopOwnedByCurrentMember={isShopOwnedByCurrentMember}
                    />
                </div>
            ))}
        />
    );
};

export const ComponentsWithStitchAds = ({
    adsEnabled,
    components,
    itemLayout,
    pageIndex,
    siteLayout,
    skipTopAd
}) => {
    const result = [];
    let mobileAdIndex = 0;
    let tabletAdIndex = 0;
    let desktopAdIndex = 0;

    const isExtraLargeDesktop = useMediaQuery({
        minWidth: BREAKPOINT_SIZES.XL.min
    });

    if (!(components && components.length > 0)) {
        return null;
    }

    const adFrequency = getAdFrequency(
        isExtraLargeDesktop,
        itemLayout,
        siteLayout
    );

    for (const [index, component] of components.entries()) {
        if (adsEnabled && pageIndex > 0) {
            if ((index - MOBILE_AD_OFFSET) % adFrequency === 0) {
                result.push(
                    <MobileAd
                        key={`mobile-ad-${mobileAdIndex}-${pageIndex}`}
                        index={mobileAdIndex}
                    />
                );
                mobileAdIndex += 1;
            }
            if ((index - TABLET_AD_OFFSET) % adFrequency === 0) {
                if (skipTopAd && index === 0) {
                    desktopAdIndex += 1;
                } else {
                    result.push(
                        <TabletAd
                            key={`tablet-ad-${tabletAdIndex}-${pageIndex}`}
                            index={tabletAdIndex}
                            autoscale={true}
                        />
                    );
                    tabletAdIndex += 1;
                }
            }
            if ((index - DESKTOP_AD_OFFSET) % adFrequency === 0) {
                if (skipTopAd && index === 0) {
                    desktopAdIndex += 1;
                } else {
                    result.push(
                        <DesktopAd
                            autoscale={true}
                            index={desktopAdIndex}
                            key={`desktop-ad-${desktopAdIndex}-${pageIndex}`}
                        />
                    );
                    desktopAdIndex += 1;
                }
            }
        }
        result.push(component);

        //Add extra ad to the end for desktop if frequency 16
        if (index === components.length - 1 && adFrequency === 16) {
            result.push(
                <DesktopAd
                    autoscale={true}
                    index={desktopAdIndex}
                    key={`desktop-ad-${desktopAdIndex}-${pageIndex}`}
                />
            );
            desktopAdIndex += 1;
        }
    }

    return result;
};

export default ItemsWithStitchedAds;
